import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import request from '@/utils/request'

export const useHomeworkStore = defineStore('homework', () => {
  // 状态 (State)
  const workInfo = ref(null)
  const workDetail = ref(null)
  const savedParams = ref({
    schoolId: '',
    studentId: '',
    courseId: '',
    nodeId: '',
    workId: '',
    title: '',
    createUserId: '',
    classId: '',
    paperId: ''
  })
  const isLoading = ref(false)
  const error = ref(null)
  
  // 倒计时相关状态
  const countdownText = ref('') // 倒计时文本
  let countdownTimer = null // 倒计时定时器

  // 计算属性 (Getters)
  const getWorkInfo = computed(() => workInfo.value)
  const getWorkDetail = computed(() => workDetail.value)
  const getSavedParams = computed(() => savedParams.value)
  const getCountdownText = computed(() => countdownText.value) // 倒计时文本计算属性

  // 动作 (Actions)
  // 设置初始参数
  const setParams = (params) => {
    savedParams.value = { ...savedParams.value, ...params }
  }

  // 获取作业列表
  // 修改1: 保存完整的作业列表而不仅仅是第一个元素
  const fetchWorkList = async () => {
    try {
      const { schoolId, studentId, courseId, nodeId } = savedParams.value
      
      // 详细检查每个参数
      if (!schoolId || !studentId || !courseId || !nodeId) {
        throw new Error('缺少必要参数')
      }
      
      isLoading.value = true
      error.value = null
      
      const response = await request({
        url: '/user/yee_course_student_work_list',
        method: 'get',
        params: {
          schoolId: parseInt(schoolId),
          studentId: parseInt(studentId),
          courseId: parseInt(courseId),
          nodeId: parseInt(nodeId)
        }
      })
      
      console.log('作业列表接口返回:', response)
      
      if (response.code === 200 && response.data?.workInfo && response.data.workInfo.length > 0) {
        // 保存完整的作业列表，而不仅仅是第一个元素
        workInfo.value = response.data.workInfo
        
        // 仍然使用第一个元素作为默认作业
        const defaultWork = response.data.workInfo[0]
        
        // 保存需要的参数
        savedParams.value = {
          ...savedParams.value,
          workId: defaultWork.workId,
          title: defaultWork.title
        }
      }
      
      return workInfo.value
    } catch (err) {
      console.error('获取作业列表失败:', err)
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // 获取作业详情
  const fetchWorkDetail = async () => {
    try {
      const { schoolId, studentId, courseId, workId, title } = savedParams.value
      
      // 详细检查每个参数
      if (!schoolId || !studentId || !courseId || !workId || !title) {
        throw new Error('缺少必要参数')
      }
      
      isLoading.value = true
      error.value = null
      
      // 第一步：从保存的作业列表中找到对应workId的作业
      const currentWork = Array.isArray(workInfo.value) 
        ? workInfo.value.find(item => String(item.workId) === String(workId))
        : null
        
      // 立即创建一个初始的workDetail对象，设置正确的frequency状态
      if (currentWork) {
        workDetail.value = {
          ...currentWork
        }
        console.log('立即设置初始workDetail，frequency为:', workDetail.value.frequency)
      } else {
        // 如果找不到对应作业，才重置为null
        workDetail.value = null
      }
      
      // 第二步：发起API请求获取详细数据
      const response = await request({
        url: '/user/yee_course_student_work_detail',
        method: 'get',
        params: {
          schoolId: parseInt(schoolId),
          studentId: parseInt(studentId),
          workId: parseInt(workId),
          courseId: parseInt(courseId),
          title: title
        }
      })
      
      // 详细地记录返回数据的结构
      console.log('作业详情接口返回:', response)
      
      // 第三步：处理响应数据
      if (response.code === 200 && response.data) {
        let apiWorkDetail = null
        
        // 尝试从不同位置提取workDetail
        if (response.data.workDetail) {
          if (Array.isArray(response.data.workDetail)) {
            apiWorkDetail = response.data.workDetail[0] || null;
          } else if (typeof response.data.workDetail === 'object' && response.data.workDetail !== null) {
            apiWorkDetail = response.data.workDetail;
          }
        }
        
        // 如果获取不到，使用整个data对象
        if (!apiWorkDetail) {
          apiWorkDetail = response.data;
        }
        
        // 第四步：合并数据 - 保留初始设置的frequency，合并API返回的其他数据
        if (apiWorkDetail) {
          workDetail.value = {
            ...workDetail.value,  // 保留初始的frequency值
            ...apiWorkDetail      // 合并API返回的其他数据
          }
        }
        
        console.log('合并后的workDetail值:', workDetail.value);
        
        // 第五步：确保frequency字段正确设置
        if (workDetail.value) {
          // 再次从workInfo数组中查找对应workId的作业项，确保获取最新值
          const currentWorkFromList = Array.isArray(workInfo.value) 
            ? workInfo.value.find(item => String(item.workId) === String(workId))
            : null;
          
          // 获取所有可能的frequency来源
          const frequencyFromInitial = workDetail.value.frequency;  // 初始设置的值
          const frequencyFromWorkInfo = currentWorkFromList?.frequency;
          
          // 最终使用的frequency值
          const finalFrequency = frequencyFromInitial ?? frequencyFromWorkInfo ?? null;
          
          // 确保frequency字段存在
          workDetail.value = {
            ...workDetail.value,
            frequency: finalFrequency
          };
          
          console.log('最终设置的frequency值:', finalFrequency);
          
          // 保存其他参数
          savedParams.value = {
            ...savedParams.value,
            createUserId: workDetail.value.createUserId || '',
            classId: workDetail.value.classId || '',
            paperId: workDetail.value.paperId || '',
            studentId: workDetail.value.studentId || savedParams.value.studentId
          }
            
          updateCountdown()
        }
      }
      
      return workDetail.value
    } catch (err) {
      console.error('获取作业详情失败:', err)
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }
  
  // 安全解析日期的工具函数
  const parseDate = (dateString) => {
    if (!dateString) return NaN
    
    // 尝试多种解析方式
    const formats = [
      // 标准ISO格式
      dateString,
      // 兼容iOS的格式 (将-替换为/)
      dateString.replace(/-/g, '/'),
      // ISO 8601格式
      dateString.replace(' ', 'T') + 'Z',
      // 兼容iOS的ISO格式
      dateString.replace(/-/g, '/').replace(' ', 'T') + 'Z'
    ]
    
    for (const format of formats) {
      try {
        const date = new Date(format)
        const time = date.getTime()
        if (!isNaN(time)) {
          return time
        }
      } catch (error) {
        // 忽略解析错误，尝试下一种格式
      }
    }
    
    return NaN
  }
  
  // 倒计时相关方法
  const updateCountdown = () => {
    const workDetail = getWorkDetail.value
    if (!workDetail?.endTime) {
      countdownText.value = '24天24小时24分24秒' // 默认值
      return
    }
    
    // 安全解析日期字符串
    const endTime = parseDate(workDetail.endTime)
    
    // 检查endTime是否有效
    if (isNaN(endTime)) {
      console.error('无法解析结束时间:', workDetail.endTime)
      countdownText.value = '24天24小时24分24秒' // 默认值
      return
    }
    
    const nowTime = new Date().getTime()
    const diff = endTime - nowTime
    
    // 如果已过期
    if (diff <= 0) {
      countdownText.value = '已截止'
      stopCountdown() // 过期后停止倒计时
      return
    }
    
    // 计算天、时、分、秒
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((diff % (1000 * 60)) / 1000)
    
    // 格式化显示
    countdownText.value = `${days}天${hours}小时${minutes}分${seconds}秒`
  }
  
  // 开始倒计时
  const startCountdown = () => {
    // 先清除可能存在的定时器
    if (countdownTimer) {
      clearInterval(countdownTimer)
    }
    
    // 立即更新一次
    updateCountdown()
    
    // 每秒更新一次
    countdownTimer = setInterval(updateCountdown, 1000)
  }
  
  // 停止倒计时
  const stopCountdown = () => {
    if (countdownTimer) {
      clearInterval(countdownTimer)
      countdownTimer = null
    }
  }
  
  // 重置状态时也要停止倒计时
  const resetState = () => {
    workInfo.value = null
    workDetail.value = null
    countdownText.value = ''
    stopCountdown() // 停止倒计时
    savedParams.value = {
      schoolId: '',
      studentId: '',
      courseId: '',
      nodeId: '',
      workId: '',
      title: '',
      createUserId: '',
      classId: '',
      paperId: ''
    }
    error.value = null
  }

  return {
    // 状态
    workInfo,
    workDetail,
    savedParams,
    isLoading,
    error,
    countdownText,
    // 计算属性
    getWorkInfo,
    getWorkDetail,
    getSavedParams,
    getCountdownText,
    // 动作
    setParams,
    fetchWorkList,
    fetchWorkDetail,
    updateCountdown,
    startCountdown,
    stopCountdown,
    resetState
  }
})